import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return matches;
}

export const useIsMobile = () => useMediaQuery('(max-width: 767px)');
export const useIsWide = () => useMediaQuery('(min-width: 1180px)');

/**
 * Parses /app/<space or @me>/<channel>, /app/servers/… for game servers and /app/support/… (both part of home,
 * beside the DMs), /app/admin/… and /app/discover.
 */
export function useAppRoute(): { spaceId: string | null; channelId: string | null; home: boolean; servers: boolean; support: boolean; admin: boolean; discover: boolean } {
  const { pathname } = useLocation();
  const [, app, first, second] = pathname.split('/');
  const none = { spaceId: null, channelId: null, home: false, servers: false, support: false, admin: false, discover: false };
  if (app !== 'app') return { ...none, home: true };
  if (first === 'servers' || first === 'minecraft') return { ...none, home: true, servers: true };
  if (first === 'support') return { ...none, home: true, support: true };
  if (first === 'admin') return { ...none, admin: true };
  if (first === 'discover') return { ...none, discover: true };
  if (!first || first === '@me') return { ...none, channelId: second ?? null, home: true };
  return { ...none, spaceId: first, channelId: second ?? null };
}

export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function copyText(text: string): Promise<void> {
  return navigator.clipboard?.writeText(text) ?? Promise.reject(new Error('Clipboard unavailable'));
}
